import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { EASE_OUT } from "../../kit.jsx";

export const MAIL = "founder@projectgrid.org";

export const SOCIALS = {
  instagram: "https://www.instagram.com/projectgrid_org/",
  linkedin: "https://www.linkedin.com/company/project-grid-global",
};

export function mailto(subject, body) {
  const parts = [];
  if (subject) parts.push(`subject=${encodeURIComponent(subject)}`);
  if (body) parts.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${MAIL}${parts.length ? `?${parts.join("&")}` : ""}`;
}

/* Button with the homepage text roll. Internal paths use Link, everything else an anchor. */
export function RollButton({ to, href, label, variant = "fill", icon = null, className = "", external = false }) {
  const classes = `gh-btn gh-btn--${variant} ${className}`;
  const inner = (
    <>
      {icon}
      <span className="roll" data-label={label}><span>{label}</span></span>
    </>
  );
  if (to) return <Link className={classes} to={to}>{inner}</Link>;
  return (
    <a className={classes} href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
      {inner}
    </a>
  );
}

/* Fades and lifts an element once on mount. Used for hero copy under a MaskWords heading. */
export function Rise({ as = "div", delay = 0, className = "", children, ...rest }) {
  const reduce = useReducedMotion();
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      initial={reduce ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE_OUT }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* Copies the contact address and confirms in place. Falls back to selecting nothing if the clipboard is blocked. */
export function CopyEmailButton({ className = "" }) {
  const [state, setState] = useState("idle");
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  async function copy() {
    try {
      await navigator.clipboard.writeText(MAIL);
      setState("copied");
    } catch {
      setState("failed");
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), 2400);
  }
  const label = state === "copied" ? "Copied" : state === "failed" ? "Copy blocked. Select the address." : "Copy address";
  return (
    <button type="button" className={`gh-btn gh-btn--ghost jn-copy ${className}`} onClick={copy} aria-live="polite">
      {label}
    </button>
  );
}

/* Scroll reveal that renders the list item itself, so lists stay valid HTML. */
export function RevealItem({ as = "li", delay = 0, className, children }) {
  const reduce = useReducedMotion();
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, delay, ease: EASE_OUT }}
    >
      {children}
    </Tag>
  );
}
